import type { PillTone } from '@bcecloud/portal-ui';

/**
 * Maps a Salesforce event stage to a status-pill tone, so contract status
 * reads at a glance and consistently across the app (list, detail, assistant
 * cards). One place so a new stage is coloured once, not three times.
 *
 * Exact matches first; then substring rules so an unseen stage still lands on
 * a sensible colour instead of grey. Green = committed, red = dead/cancelled,
 * amber = paused/at-risk, blue = in progress, grey = closed-neutral.
 */
const EXACT_TONES: Readonly<Record<string, PillTone>> = {
  'Contract Signed': 'success',
  'Closed Won': 'success',
  'Signed - Postponed': 'warning',
  'On Hold': 'warning',
  'Decision to Wait': 'warning',
  'Cancelled-Rebooked': 'warning',
  Cancelled: 'danger',
  'Closed Lost': 'danger',
  'Awaiting Signature': 'info',
  'Proposal(s) Sent': 'info',
  'Program Selected': 'info',
  'Needs Identified': 'info',
  'Internal Review': 'info',
  'Closed - Duplicate': 'neutral',
  'Closed - Unqualified': 'neutral',
  Unresponsive: 'neutral',
};

/** Ordered substring fallbacks — first match wins. */
const FALLBACK_RULES: readonly (readonly [RegExp, PillTone])[] = [
  [/rebook|postpon|on hold|hold|wait/i, 'warning'],
  [/cancel|lost|declin/i, 'danger'],
  [/signed|won|complete|confirm/i, 'success'],
  [/proposal|await|review|selected|identified|sent|pending/i, 'info'],
];

/**
 * The pill tone for an event stage.
 *
 * @param stage - The stage name (`event.stageName`), possibly undefined.
 * @returns A pill tone; neutral for unknown or missing stages.
 */
export function eventStageTone(stage: string | undefined | null): PillTone {
  if (!stage) return 'neutral';
  const exact = EXACT_TONES[stage];
  if (exact) return exact;
  for (const [pattern, tone] of FALLBACK_RULES) {
    if (pattern.test(stage)) return tone;
  }
  return 'neutral';
}
